import { CloseOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync, { OptionType } from '@nxsys/components/AppSelectSync';
import {
  getPaymentModelLabel,
  getPaymentModelOnboardingLabel,
  paymentModelOptionsIreland,
} from '@nxsys/constants/AppConstants';
import { PROSPECT_TYPE } from '@nxsys/constants/prospect';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND, UNITED_KINGDOM } from '@nxsys/store/constants/keys';
import {
  GetCandidateListParams,
  GetProspectListParams,
} from '@nxsys/store/slices/prospect/types';
import { getAppConfig } from '@nxsys/utils';
import { hasFilterValue } from '@nxsys/utils/filterForm';
import { Form } from 'antd';
import { FormProps } from 'antd/lib';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

type FilterModalProps = {
  isOpen: boolean;
  title: string;
  // Only the Candidates and Onboarding lists filter — an agency prospect holds
  // no employer, agency or payment model of its own
  type: PROSPECT_TYPE;
  toggle: () => void;
  onFilter: (params: GetProspectListParams & GetCandidateListParams) => void;
};

const FilterModal: React.FC<FilterModalProps> = ({
  title,
  isOpen,
  type,
  toggle,
  onFilter,
}: FilterModalProps) => {
  const { messages } = useTypedIntl();

  const { isBureau, isBureauEmployer, isAgency, payrollCountry, user } =
    useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;
  const isUK = isBureau && payrollCountry === UNITED_KINGDOM;

  const isOnboarding = type === PROSPECT_TYPE.ONBOARDING;

  const { candidateList, prospectList } = useSelector(
    (state: AppState) => state.prospectStore,
  );
  const queryParams = isOnboarding
    ? prospectList.queryParams
    : candidateList.queryParams;

  const [form] = Form.useForm();
  const watchValue = Form.useWatch([], form);
  const hasFilter = hasFilterValue(watchValue);
  // Keep Apply enabled once a filter is applied, so Clear All + Apply can remove it.
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);

  const defaultPaymentModel = getAppConfig('paymentModels');
  const paymentModelOptions = useMemo(() => {
    // Ireland has its own two-value list, and RCT is stored as the BE value CIS
    if (isIreland) return paymentModelOptionsIreland;
    // Label them exactly as the column they filter does
    const getLabel = isOnboarding
      ? getPaymentModelOnboardingLabel
      : getPaymentModelLabel;
    return (defaultPaymentModel ?? []).map(({ value }) => ({
      value,
      label: getLabel(value),
    }));
  }, [defaultPaymentModel, isIreland, isOnboarding]);

  const filterOptions: {
    label: string;
    name: string;
    filterType: string;
    placeholder?: string;
    options?: { label: string; value: string }[];
    selectSyncType?: string;
    apiType?: string;
    paramFilter?: Record<string, any>;
  }[] = useMemo(
    () => [
      {
        // Same label the column carries: "Employer Name" for bureau,
        // "Company Name" otherwise
        label: isBureau
          ? messages['common.bureau.companyName']
          : messages['common.companyName'],
        name: 'companyList',
        filterType: 'selectSync',
        selectSyncType: 'company',
        apiType: 'searchCompany',
        placeholder: isBureau
          ? messages['form.selectEmployer']
          : messages['form.selectCompany'],
        // Only companies enabled for onboarding can ever appear on these tabs,
        // and a bureau employer may only look up the companies assigned to them
        paramFilter: {
          companyUserIds: isBureauEmployer ? user?.companyUserIds : [],
        },
      },
      // Mirrors the Agency column: bureau never shows it, and an agency user
      // only ever sees its own agency on the Onboarding tab
      ...(!isBureau && !(isOnboarding && isAgency)
        ? [
            {
              label: messages['common.agencyName'],
              name: 'agencyList',
              filterType: 'selectSync',
              // A candidate may sit against a live agency or an agency
              // prospect, so its picker offers both; a prospect only ever
              // points at a live agency
              selectSyncType: isOnboarding ? 'agency' : 'agency&prospect',
              apiType: isOnboarding ? 'searchAgency' : 'searchAgencyAndProspect',
              placeholder: messages['common.selectAgency'],
            },
          ]
        : []),
      // A UK bureau only ever runs PAYE, so a payment model filter there would
      // offer a single value on both lists
      ...(!isUK
        ? [
            {
              label: messages['common.paymentModel'],
              name: 'paymentModel',
              filterType: 'select',
              options: paymentModelOptions,
              placeholder: messages['common.selectPaymentModel'],
            },
          ]
        : []),
    ],
    [
      messages,
      isBureau,
      isBureauEmployer,
      isAgency,
      isUK,
      isOnboarding,
      paymentModelOptions,
      user?.companyUserIds,
    ],
  );

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    form.resetFields();
  };

  const onFinish: FormProps['onFinish'] = (values) => {
    const params = {
      ...queryParams,
      page: 0,
      companyIds: values?.companyList?.length
        ? values.companyList.map((company: OptionType) => company.value)
        : undefined,
      agencyIds: values?.agencyList?.length
        ? values.agencyList.map((agency: OptionType) => agency.value)
        : undefined,
      // AppSelect is labelInValue, so the field holds the whole option
      paymentModel: values?.paymentModel?.value || undefined,
    };
    setHasAppliedFilter(hasFilterValue(values));
    onFilter(params);
    toggle();
  };

  const onFinishFailed: FormProps['onFinishFailed'] = () => {
    // Failed validation handled by Ant Design Form
  };

  return (
    <AppModalContainer
      title={title}
      isOpen={isOpen}
      toggle={toggle}
      minHeight={`${120 + filterOptions.length * 90}px`}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.apply'],
          buttonType: 'primary',
          onClick: () => form.submit(),
          disabled: !hasFilter && !hasAppliedFilter,
        },
      ]}
      closeIcon={
        <div style={{ marginRight: 90 }}>
          <AppButton
            name={messages['common.clearAll']}
            leftIcon={<CloseOutlined />}
            onClick={handleClearAll as any}
            disabled={!hasFilter}
          />
        </div>
      }
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        {filterOptions?.map((f) => (
          <Form.Item key={f.name} label={f.label} name={f.name} layout="vertical">
            {f.filterType === 'select' && (
              <AppSelect placeholder={f.placeholder} options={f.options ?? []} />
            )}
            {f.filterType === 'selectSync' && (
              <AppSelectSync
                type={f.selectSyncType ?? ''}
                apiType={f.apiType as any}
                placeholder={f.placeholder}
                paramFilter={f.paramFilter}
                onChangeValue={(value: OptionType | OptionType[] | null) => {
                  form.setFieldsValue({
                    [f.name]: value,
                  });
                }}
                mode={'multiple'}
              />
            )}
          </Form.Item>
        ))}
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default FilterModal;
